import React from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { CardActionArea } from "@mui/material";

import SwipeableImageContainer from "components/image-containers/SwipeableImageContainer/SwipeableImageContainer.component";
import CustomNextLink from "components/links/CustomNextLink/CustomNextLink.component";
import VehicleSaleTypeChip from "components/chips/VehicleSaleTypeChip/VehicleSaleTypeChip.component";
import Label from "components/typographies/Label/Label.component";

import {
  formatOdometerString,
  formatPriceString,
  formatVehicleName,
} from "helpers/string-format-helpers";
import usePageWidth from "hooks/usePageWidth.hook";

// Styles
import { useStyles } from "./ListingCard.styles";

dayjs.extend(updateLocale);
dayjs.extend(relativeTime);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few seconds",
    m: "1m",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    M: "1m",
    MM: "%dm",
    y: "1y",
    yy: "%dy",
  },
});

const ListingCard = ({ listing, dataCy, onClick = () => {}, href = "#" }) => {
  const classes = useStyles();
  const { vehicle, createdAt } = listing;
  const { isDesktopWidth } = usePageWidth();
  const createdAtFormatted = createdAt?.replace(" UTC", "Z");

  const buildDataCy = () =>
    `${dataCy}-${listing.seller.sellerType}${
      listing.seller.sellerType === "private" ? `-${listing.negotiable}` : ""
    }`;

  return (
    <Card
      data-listing-id={listing.id}
      data-cy={buildDataCy()}
      data-component="ListingCard"
      onClick={onClick}
      className={classes.root}
    >
      <CardActionArea
        component="div"
        classes={{ focusHighlight: classes.focusHighlight }}
      >
        <CustomNextLink
          color="black"
          data-cy="listing-card-link"
          dontUnderlineText
          href={href}
          target="_blank"
        >
          <div className={classes.imageContainer}>
            <div className={classes.imageChip}>
              <VehicleSaleTypeChip listing={listing} isDesktop />
            </div>
            <SwipeableImageContainer
              showButtons
              mouseEvents={isDesktopWidth}
              vehicle={vehicle}
              width="100vw"
              size="medium"
              priority
              variant="text"
              imageClassName={classes.swipeableImage}
            />
          </div>
          <CardContent classes={{ root: classes.cardContent }}>
            <div className={classes.titleRow}>
              <Label
                size="lg"
                weight="semibold"
                data-cy="listing-card-year-make-model"
              >
                {formatVehicleName(vehicle, { includeYear: true })}
              </Label>
              <div className="flexGrow" />
              <Label size="md" weight="light" color="text.tertiary">
                {dayjs().to(createdAtFormatted, true)}
              </Label>
            </div>
            <Label
              size="lg"
              weight="light"
              sx={{ marginBottom: "2px" }}
              data-cy="listing-card-location-kilometers"
            >
              {`${listing.address.city}, ${
                listing.address.province
              } · ${formatOdometerString(listing.kilometers)}`}
            </Label>
            <Label size="lg" weight="semibold" data-cy="listing-card-price">
              {formatPriceString({ price: listing.price })}
            </Label>
          </CardContent>
        </CustomNextLink>
      </CardActionArea>
    </Card>
  );
};

export default ListingCard;
